import { motion } from "framer-motion";
import { BookOpen, GraduationCap, Trophy, Target, CheckCircle2 } from "lucide-react";
import { assetUrl } from "@/lib/api";

const ICONS = { BookOpen, GraduationCap, Trophy, Target };

export const AboutUs = ({ content }) => {
  const a = content.about;
  return (
    <section id="tentang-kami" data-testid="about-us-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-sky-900/10">
              <img src={assetUrl(a.image1)} alt="Tutor membimbing siswa" className="w-full h-[420px] object-cover" />
            </div>
            {a.image2 && (
              <div className="absolute -bottom-8 -left-6 w-48 h-56 rounded-2xl overflow-hidden shadow-xl border-4 border-white hidden sm:block">
                <img src={assetUrl(a.image2)} alt="Suasana kelas" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="absolute -top-6 -right-6 w-28 h-28 rounded-2xl bg-amber-400 shadow-xl flex flex-col items-center justify-center text-slate-900 animate-float">
              <span className="text-3xl font-extrabold">{a.yearsBadge}</span>
              <span className="text-[11px] font-semibold text-center px-2 leading-tight">Tahun Berdedikasi</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-100 text-sky-700 text-sm font-semibold mb-4">
              Tentang Kami
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              {a.title}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">{a.body}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {a.features.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-sky-50 border border-sky-100">
                  <CheckCircle2 className="w-5 h-5 text-sky-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{f}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Program Unggulan Kami</h3>
            <p className="mt-3 text-slate-500">
              Pilihan program belajar yang disesuaikan dengan jenjang dan kebutuhan setiap siswa.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.programs.map((p, i) => {
              const Icon = ICONS[p.icon] || BookOpen;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  data-testid={`program-card-${i}`}
                  className="group p-7 rounded-2xl bg-white border border-slate-100 shadow-lg shadow-slate-200/40 hover:-translate-y-2 hover:shadow-xl hover:border-sky-200 transition-all"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
